import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { toast } from "react-toastify";
import {
  Button,
  ButtonLink,
  Column,
  InputCnpjCpf,
  InputEmail,
  InputPhoneNumber,
  InputSecret,
  InputText,
  Row,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import { escapeHtml } from "@components/ow";
import HelpVideoButton from "@components/HelpVideoButton";
import { setActiveCompanyId } from "@helpers/activeCompany";
import { invitationError, validateEmployeeSignUp } from "@helpers/signUp";
import { focusFirstError } from "../Common/FormPage";
import { PublicBox, PublicBoxFooter, PublicFields, PublicLoading, PublicPage, submitOnEnter } from "../Common/PublicPage";
import PasswordRequirements from "./PasswordRequirements";

const companyHtml = (name) => (name ? `<strong>${escapeHtml(name)}</strong>` : "");

// Convite do colaborador: cadastro novo ou, para quem já está logado, o vínculo com a empresa
export default function EmployeeSignUp() {
  const authService = useService("auth");
  const navigate = useNavigate();
  const { loginWithRedirect, isAuthenticated, isLoading: authLoading } = useAuth0();
  const { token } = useParams();
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenError, setTokenError] = useState(null);
  const [companyName, setCompanyName] = useState(null);
  const [linkingMode, setLinkingMode] = useState(false);
  const [linkResult, setLinkResult] = useState(null);
  // Os erros aparecem nos campos depois da primeira tentativa e se atualizam a cada mudança
  const [submitted, setSubmitted] = useState(false);
  const [state, setState] = useState({
    name: "",
    email: "",
    pass: "",
    role: "EMP",
    company: null,
    document: "",
    phone: "",
    token: token,
  });

  const errors = submitted ? validateEmployeeSignUp(state) : {};
  const change = (field) => (e) => setState((prev) => ({ ...prev, [field]: e.target.value }));

  async function loadInvitation() {
    const result = await authService.validateInvitation(token);
    if (result && result.valid) {
      setCompanyName(result.company_name || null);
      return result;
    }
    setTokenError(invitationError(result));
    return null;
  }

  useEffect(() => {
    // Verificar se retornou de login com token de convite salvo
    const savedInviteToken = localStorage.getItem("invite_token_ow");
    if (savedInviteToken && isAuthenticated && !authLoading) {
      localStorage.removeItem("invite_token_ow");
      // Está logado e tem token de convite — entrar em modo linking
      setLinkingMode(true);
      setValidating(false);
      loadInvitation();
      return;
    }

    async function validate() {
      const result = await loadInvitation();
      if (result) {
        setState((prev) => ({ ...prev, company: result.company_id }));

        // Se está logado, mostrar modo linking
        if (isAuthenticated && !authLoading) {
          setLinkingMode(true);
        }
      }
      setValidating(false);
    }

    if (!authLoading) {
      validate();
    }
  }, [token, isAuthenticated, authLoading]);

  function handleLoginRedirect() {
    // Salvar token de convite e redirecionar para Auth0 login
    localStorage.setItem("invite_token_ow", token);
    loginWithRedirect({
      appState: { returnTo: `/register/e/${token}` },
    });
  }

  async function handleLinkCompany() {
    setLoading(true);
    try {
      const result = await authService.linkCompany(token);
      if (result && !result.error) {
        setLinkResult("success");
        toast.success("Vinculado com sucesso!");
        // Atualizar companyId e redirecionar
        if (result.data && result.data.companyId) {
          setActiveCompanyId(result.data.companyId);
        }
        setTimeout(() => {
          navigate("/");
        }, 2000);
      } else {
        const msg = result?.msg || "Erro ao vincular";
        if (msg.includes("já está vinculado")) {
          setLinkResult("already_linked");
        } else {
          toast.error(msg);
        }
      }
    } catch (err) {
      const errorMsg = err?.response?.data?.msg;
      if (errorMsg && errorMsg.includes("já está vinculado")) {
        setLinkResult("already_linked");
      } else {
        toast.error("Erro ao vincular empresa");
      }
    }
    setLoading(false);
  }

  async function handleSubmit() {
    if (loading) return;
    setSubmitted(true);
    const found = validateEmployeeSignUp(state);
    if (Object.keys(found).length) {
      focusFirstError(Object.keys(found));
      return;
    }

    setLoading(true);

    try {
      const response = await authService.conn.axios.post("auth/add-user", state);
      if (response.data && response.data.error) {
        toast.error(response.data.msg || "Erro ao cadastrar usuário");
      } else {
        // Quem já tinha conta continua com a senha de antes: a digitada aqui não é usada
        const existingAccount = response.data?.data?.existingAccount;
        if (existingAccount) {
          toast.success("Vínculo realizado! Você já tinha conta: entre com o e-mail e a senha que você já usa.", {
            autoClose: 6000,
          });
        } else {
          toast.success("Cadastro finalizado com sucesso!");
        }
        setTimeout(() => navigate("/auth/entrar"), existingAccount ? 6000 : 3000);
        return;
      }
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Erro ao cadastrar usuário");
    }
    setLoading(false);
  }

  if (validating || authLoading) {
    return <PublicLoading text="Abrindo o convite" />;
  }

  if (tokenError) {
    return (
      <PublicPage
        title={tokenError.title}
        description={tokenError.description}
        actions={
          tokenError.retry ? (
            <Button $text="Tentar novamente" $variant="primary" $action={() => window.location.reload()} />
          ) : null
        }
      />
    );
  }

  // Modo linking — usuário já está logado
  if (linkingMode && isAuthenticated) {
    if (linkResult === "already_linked") {
      return (
        <PublicPage
          title="Você já está vinculado a esta empresa"
          actions={<Button $text="Ir para o Dashboard" $variant="primary" $action={() => navigate("/")} />}
        />
      );
    }

    if (linkResult === "success") {
      return <PublicPage title="Vinculado com sucesso!" description="Redirecionando…" />;
    }

    return (
      <PublicPage
        title={`Você foi convidado pela empresa ${companyHtml(companyName)}`.trim()}
        description="Deseja se vincular a esta empresa?"
        actions={<Button $text="Vincular" $variant="primary" $action={handleLinkCompany} $isLoading={loading} />}
      />
    );
  }

  return (
    <PublicPage
      title="Cadastro de colaborador"
      description={
        companyName
          ? `Você foi convidado pela empresa ${companyHtml(companyName)}.`
          : "A plataforma feita para você como colaborador."
      }
      headerExtra={<HelpVideoButton videoKey="employeeSignUp" label="Ver como fazer o cadastro" />}
    >
      <PublicBox onKeyDown={submitOnEnter(handleSubmit)}>
        <PublicFields>
          <InputText
            $idForm="invite-name"
            $label="Nome completo"
            $variant="default"
            $errors={errors["invite-name"]}
            autoComplete="name"
            placeholder="João Silva"
            value={state.name}
            onChange={change("name")}
          />
          <Row $gap={20}>
            <Column $columns={6}>
              <InputCnpjCpf
                $idForm="invite-document"
                $label="CPF ou CNPJ"
                $variant="default"
                $errors={errors["invite-document"]}
                value={state.document}
                onChange={change("document")}
              />
            </Column>
            <Column $columns={6}>
              <InputPhoneNumber
                $idForm="invite-phone"
                $label="WhatsApp"
                $variant="default"
                $errors={errors["invite-phone"]}
                autoComplete="tel"
                placeholder="(11) 99999-9999"
                value={state.phone}
                onChange={change("phone")}
              />
            </Column>
          </Row>
          <InputEmail
            $idForm="invite-email"
            $label="E-mail"
            $variant="default"
            $errors={errors["invite-email"]}
            autoComplete="email"
            placeholder="joao@onework.com"
            value={state.email}
            onChange={change("email")}
          />
          <InputSecret
            $idForm="invite-pass"
            $label="Senha"
            $variant="default"
            $errors={errors["invite-pass"]}
            autoComplete="new-password"
            value={state.pass}
            onChange={change("pass")}
          />
          <PasswordRequirements password={state.pass} />
        </PublicFields>
        <PublicBoxFooter>
          <ButtonLink $text="Já tem cadastro? Entre para vincular" $action={handleLoginRedirect} />
          <Button $text="Cadastrar" $variant="primary" $action={handleSubmit} $isLoading={loading} />
        </PublicBoxFooter>
      </PublicBox>
    </PublicPage>
  );
}
